// Layer 3 seed: one illustrative smelter schedule for Bunker Hill — decision
// D-money. Every figure [syn]; provenance cites decisions.md § Seed.

import type { SmelterSchedule } from '@/versions/v1/schema/smelter-schedule';

const SYN = 'illustrative smelter schedule — D14, Komatsu K9';

export const BUNKER_HILL_SMELTER_SCHEDULE: SmelterSchedule = {
  id: 'bh-smelter-syn',
  currency: 'USD',
  provenance: `[syn] ${SYN}`,
  prices: {
    pbPerTonne: 2_180,
    znPerTonne: 2_920,
    agPerOz: 31.5,
    priorWeek: { pbPerTonne: 2_240, znPerTonne: 2_980, agPerOz: 31.0 },
    budget: { pbPerTonne: 2_100, znPerTonne: 2_650, agPerOz: 26.5 },
    priceDate: '2026-06-01',
    provenance: `[syn] LME month-average proxy — ${SYN}`,
    comparisonProvenance: '[syn] prior-week and budget metal price basis — no price series in the corpus',
  },
  silverOnLead: {
    payableFraction: 0.95,
    flatDeductionGt: 50,
    provenance: `[syn] 95 % of contained Ag payable less 50 g/t deduction on Pb concentrate — ${SYN}`,
  },
  products: [
    {
      product: 'Pb',
      label: 'Pb concentrate',
      payable: {
        payableFraction: 0.95,
        provenance: `[syn] 95 % of contained Pb payable — ${SYN}`,
      },
      treatmentChargePerDmt: 78,
      freightPerWmt: 42,
      moistureFraction: 0.09,
      penalties: [
        {
          element: 'As',
          limit: 0.5,
          unit: '%',
          ratePerUnit: 4_200,
          provenance: `[syn] As penalty above 0.5 % on Pb conc — ${SYN}`,
        },
        {
          element: 'Hg',
          limit: 10,
          unit: 'ppm',
          ratePerUnit: 85,
          provenance: `[syn] Hg penalty above 10 ppm — ${SYN}`,
        },
      ],
      provenance: `[syn] Pb concentrate offtake — ${SYN}`,
    },
    {
      product: 'Zn',
      label: 'Zn concentrate',
      payable: {
        payableFraction: 0.85,
        gradeDeduction: 8,
        provenance: `[syn] 85 % of Zn grade after an 8-unit deduction — ${SYN}`,
      },
      treatmentChargePerDmt: 115,
      freightPerWmt: 42,
      moistureFraction: 0.09,
      penalties: [
        {
          element: 'As',
          limit: 0.3,
          unit: '%',
          ratePerUnit: 3_800,
          provenance: `[syn] As penalty above 0.3 % on Zn conc — ${SYN}`,
        },
        {
          element: 'Pb',
          limit: 3.0,
          unit: '%',
          ratePerUnit: 140,
          provenance: `[syn] Pb in Zn penalty above 3.0 % — ${SYN}`,
        },
      ],
      provenance: `[syn] Zn concentrate offtake — ${SYN}`,
    },
  ],
};

/** Convenience projection for slider defaults; the schedule remains the source. */
/** One contractual limit for cards, charts, specs and settlement. */
export function smelterPenaltyLimit(product: 'Pb' | 'Zn', element: string): number {
  const term = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === product)
    ?.penalties.find((row) => row.element === element);
  if (!term) throw new Error(`No ${element} penalty term for ${product}`);
  return term.limit;
}

export function defaultSmelterPriceState() {
  return {
    leadPerTonne: BUNKER_HILL_SMELTER_SCHEDULE.prices.pbPerTonne,
    zincPerTonne: BUNKER_HILL_SMELTER_SCHEDULE.prices.znPerTonne,
    silverPerOz: BUNKER_HILL_SMELTER_SCHEDULE.prices.agPerOz,
  };
}
